import { type AxiosError } from 'axios'
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useOpenableControls } from '../../../../../../generic-hooks'
import { type CategoryHierarchyResponseDto } from '../CategoryHierarchyResponseDto.type'

import { useCategoryHierarchyNavigationSwr } from './swr/useCategoryHierarchyNavigationSwr'

type CategoriesNavigationContext = {
  animationDirection?: 'backward' | 'forward' | null
  currentCategoryData: CategoryHierarchyResponseDto | null | undefined
  error: AxiosError | undefined
  handleClose: () => void
  handleOpen: () => void
  isLoading: boolean
  isOpen: boolean
  navigateToCategory: (
    categoryId: string,
    animateDirection: 'backward' | 'forward' | null,
  ) => void
  previouseCategoryData: CategoryHierarchyResponseDto | null | undefined
}

const CategoriesNavigationContext = createContext<CategoriesNavigationContext>({
  animationDirection: null,
  currentCategoryData: null,
  error: undefined,
  handleClose: () => null,
  handleOpen: () => null,
  isLoading: false,
  isOpen: false,
  navigateToCategory: () => null,
  previouseCategoryData: null,
})

const ANIMATION_DURATION_MS = 300

export const StoryblokCategoryNavigationProvider: FC<
  PropsWithChildren<{
    initialCategoryData: CategoryHierarchyResponseDto | null
  }>
> = ({ children, initialCategoryData }) => {
  const { handleClose, handleOpen, isOpen } = useOpenableControls()
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [previousCategoryData, setPreviousCategoryData] =
    useState<CategoryHierarchyResponseDto | null>(null)
  const [animationDirection, setAnimationDirection] = useState<
    'backward' | 'forward' | null
  >(null)

  const {
    data: currentCategoryData,
    error,
    isLoading,
  } = useCategoryHierarchyNavigationSwr(categoryId)

  const navigateToCategory = useCallback(
    (
      akeneoCode: string,
      animateDirection: 'backward' | 'forward' | null,
    ): void => {
      setPreviousCategoryData(
        categoryId ? (currentCategoryData ?? null) : initialCategoryData,
      )
      setCategoryId(akeneoCode)
      setAnimationDirection(animateDirection)
    },
    [categoryId, currentCategoryData, initialCategoryData],
  )

  useEffect(() => {
    if (currentCategoryData && previousCategoryData) {
      const timeoutId = setTimeout(() => {
        setPreviousCategoryData(null)
        setAnimationDirection(null)
      }, ANIMATION_DURATION_MS)

      return (): void => {
        clearTimeout(timeoutId)
      }
    }

    return undefined
  }, [currentCategoryData, previousCategoryData])

  const values = useMemo(
    () => ({
      animationDirection,
      currentCategoryData: categoryId
        ? currentCategoryData
        : initialCategoryData,
      error,
      handleClose,
      handleOpen,
      isLoading,
      isOpen,
      navigateToCategory,
      previouseCategoryData: previousCategoryData,
    }),
    [
      animationDirection,
      categoryId,
      currentCategoryData,
      initialCategoryData,
      error,
      handleClose,
      handleOpen,
      isLoading,
      isOpen,
      navigateToCategory,
      previousCategoryData,
    ],
  )

  return (
    <CategoriesNavigationContext.Provider value={values}>
      {children}
    </CategoriesNavigationContext.Provider>
  )
}

export const useCategoriesNavigationContext = (): CategoriesNavigationContext =>
  useContext(CategoriesNavigationContext)
